import { useRef, useState, type ChangeEvent } from "react";
import { ImagePlus, Loader } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { captureException } from "@/lib/analytics";
import { uploadAsset } from "@/lib/uploadAsset";
import { ImageUploadError } from "./errors";

interface FpImageUploadProps {
  /** Registers a freshly uploaded asset id with its local blob for preview. */
  onLocalAsset: (assetId: string, file: File) => void;
  /** Writes the uploaded asset id onto the selected fp-image block. */
  onAssetUploaded: (assetId: string) => void;
  /** Reports upload start and finish so the editor can lock while in flight. */
  onUploadingChange: (isUploading: boolean) => void;
}

/**
 * File picker that uploads an image and binds its asset id to the fp-image block.
 * @param onLocalAsset - Registers the uploaded asset id and file for instant preview
 * @param onAssetUploaded - Writes the uploaded asset id onto the block
 * @param onUploadingChange - Reports whether an upload is in flight
 * @returns The upload button with a hidden file input
 */
export function FpImageUpload({
  onLocalAsset,
  onAssetUploaded,
  onUploadingChange,
}: FpImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) {
      return;
    }

    setIsUploading(true);
    onUploadingChange(true);
    try {
      const asset = await uploadAsset(file);
      onLocalAsset(asset.id, file);
      onAssetUploaded(asset.id);
    } catch (error) {
      toast.error(`Could not upload ${file.name}. The image was not attached.`);
      captureException(
        new ImageUploadError(
          error instanceof Error ? error.message : "Failed to upload image",
        ),
      );
    } finally {
      setIsUploading(false);
      onUploadingChange(false);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
        disabled={isUploading}
      />
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? (
          <Loader className="size-4 animate-spin" />
        ) : (
          <ImagePlus className="size-4" />
        )}
        Upload image
      </Button>
    </div>
  );
}
